import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Button, Modal, Select, Spin, message} from 'antd'
import {PlusOutlined} from '@ant-design/icons'
import moment from 'moment'
import {
    getMaterials,
    getStageDetail,
    getStages,
    saveStageGroups,
} from '../../../../services/api/apiProductionPlanning'
import {getLocalData, setLocalData} from '../../../../services/StorageServices'
import {
    DEFAULT_GROUP_LABEL,
    GROUP_NAME_MAX_LENGTH,
    PACING_LABELS,
    RATE_UNIT_LABELS,
    ROUTES,
    VAL_CODES,
    stageConfigPath,
} from '../../constants'
import {dateOnlyIso, formatDate, formatInt, rateChangeNeedsReason, rateDeltaPercent} from '../../helpers/format'
import {
    effectivePriority,
    findConflicts,
    isDefaultGroup,
    isEffectiveAt,
    isManualPriority,
    validateGroups,
    copyGroupName,
} from '../../helpers/priority'
import {errorCode, errorDetails, errorMessage, errorStatus, hasDetailCode} from '../../helpers/apiError'
import {canEditStandardConfig} from '../../helpers/permission'
import Notice from '../shared/Notice'
import SkuIdList from '../shared/SkuIdList'
import LoadError from '../shared/LoadError'
import ActionCodes from '../shared/ActionCodes'
import GroupCard from './GroupCard'
import PasteSkuModal from './PasteSkuModal'
import HistoryDrawer from './HistoryDrawer'
import ChangeReasonModal from './ChangeReasonModal'
import useSkuOptions from '../../helpers/useSkuOptions'

const {Option} = Select

const RATE_CHANGE_THRESHOLD_PERCENT = 30

/** Khoá ghi nhớ lựa chọn ẩn/hiện gợi ý. `StorageServices` tự thêm tiền tố `com.pdf126.`. */
const HINTS_HIDDEN_KEY = 'm16.hintsHidden'

/**
 * SCR-M16-002 — Cấu hình công đoạn.
 *
 * Màn hình quan trọng nhất của M16. Mục tiêu thiết kế: người dùng nhìn một lần là biết công đoạn
 * này đang có những cấu hình gì. Vì vậy **không phân trang** (BR-M16-33) — toàn bộ nhóm nằm trên
 * một màn hình.
 *
 * Lưu theo đơn vị **cả công đoạn**, một lần ghi trọn vẹn (BR-M16-34): một nhóm sai thì không nhóm
 * nào được ghi (AC-2.4).
 */
/**
 * Gộp các dòng NVL trùng `materialCode`.
 *
 * Danh mục đang trả về mã lặp — 92 dòng nhưng chỉ 83 mã khác nhau, và 5 mã trong đó mang HAI TÊN
 * KHÁC HẲN nhau (`04` vừa là "A3D-ME-AO" vừa là "Thủy tinh"). Đây là lỗi dữ liệu ở danh mục, không
 * phải lỗi hiển thị.
 *
 * Phải gộp ở đây vì hai lý do:
 *
 * 1. Nhóm điều kiện chỉ lưu `materialCodes` (mã), không lưu id. Hai NVL cùng mã là MỘT với tính năng
 *    này —
 *    không có cách nào phân biệt lúc tra định mức, nên bày ra hai dòng chọn được là mời người dùng
 *    chọn nhầm mà không biết.
 * 2. Bày hai dòng cùng mã còn làm React trùng `key`, và trùng key thì nó dựng lại nhầm nội dung —
 *    chính là cảnh 4 dòng "04 — A3D-ME-AO" giống hệt nhau trong ô chọn.
 *
 * Mã nào có nhiều tên thì ghép tên lại (`A3D-ME-AO / Thủy tinh`) để người dùng nhìn ra chỗ lệch mà
 * báo lại, thay vì mình chọn hộ một tên rồi giấu tên kia đi.
 */
export const dedupeMaterials = (list) => {
    const byCode = new Map()

    list.forEach((item) => {
        if (!item || !item.materialCode) return

        const existing = byCode.get(item.materialCode)

        if (!existing) {
            byCode.set(item.materialCode, {...item, names: [item.name]})

            return
        }

        if (item.name && !existing.names.includes(item.name)) existing.names.push(item.name)
    })

    return [...byCode.values()].map((item) => ({...item, name: item.names.join(' / ')}))
}

/**
 * Xếp nhóm theo độ ưu tiên giảm dần — nhóm cụ thể nhất lên đầu, nhóm ALL (0 điểm) xuống cuối.
 *
 * Đây đúng thứ tự mà việc tra định mức chạy: nhiều nhóm cùng khớp một dòng NVL thì nhóm điểm cao
 * thắng. Xếp theo thứ tự đó thì đọc từ trên xuống là thấy ngay nhóm nào thắng nhóm nào.
 *
 * `effectivePriority` lo việc chọn nguồn điểm: số đặt tay nếu nhóm ở chế độ đặt tay
 * (`prioritySource`), không thì chấm theo điều kiện. Nhóm mới thêm chưa lưu thì chấm tại chỗ.
 *
 * CHỈ xếp lúc tải: xếp lại ngay khi người dùng đang gõ thì thẻ nhảy chỗ dưới con trỏ. Nhóm vừa thêm
 * nằm ở cuối tới lần tải sau, và thứ tự mảng cũng là thứ tự gửi đi lúc lưu — service quyết định nhóm
 * thắng bằng điểm chứ không bằng vị trí, nên xếp lại không làm đổi kết quả.
 */
const sortByPriority = (groups) => [...groups]
    .map((group) => ({...group}))
    .sort((left, right) => {
        const diff = effectivePriority(right) - effectivePriority(left)

        if (diff !== 0) return diff

        // Cùng điểm thì giữ thứ tự ổn định, khỏi mỗi lần tải một kiểu.
        return `${left.id || ''}`.localeCompare(`${right.id || ''}`)
    })

function StageConfigPage({match, history}) {
    const {stageCode} = match.params
    const requestedStageRef = useRef(stageCode)

    const [stageOptions, setStageOptions] = useState([])
    const [stagesError, setStagesError] = useState(null)
    const [detail, setDetail] = useState(null)
    /**
     * Công đoạn mà `detail` đang thuộc về.
     *
     * ⚠️ Đây là chốt chống HIỂN THỊ SAI DỮ LIỆU, không phải tối ưu. Đổi công đoạn là `stageCode`
     *    (từ URL) đổi NGAY, còn `detail` chỉ đổi khi request về. Trong khoảng giữa, nếu render theo
     *    `detail` cũ thì tiêu đề ghi công đoạn MỚI mà bảng nhóm là của công đoạn CŨ — người dùng
     *    tưởng công đoạn mới đã có định mức, rồi bấm Lưu là ghi nhóm của công đoạn khác sang.
     *    Trước đây cả trang bị thay bằng một vòng xoay nên không ai thấy, giờ chỉ vùng nội dung
     *    tải lại nên phải khoá tường minh.
     */
    const [detailFor, setDetailFor] = useState(null)
    const [groups, setGroups] = useState([])
    const [collapsed, setCollapsed] = useState({})
    /*
     * Ẩn phần gợi ý (chú thích xám dưới mỗi ô + khối "Ví dụ"). Người dùng đã quen thì đó là nhiễu:
     * một công đoạn có 7-8 nhóm thì phần hướng dẫn dài hơn phần dữ liệu.
     *
     * ⚠️ Lưu theo chiều `hintsHidden` (mặc định `false`), KHÔNG phải `showHints` (mặc định `true`).
     *    `getLocalData` cài `store.get(key) || defaultValue`, nên một giá trị `false` đã lưu sẽ bị
     *    NUỐT và rơi về defaultValue. Chiều kia thì bấm "Ẩn gợi ý" xong vào lại màn là gợi ý hiện
     *    lại, không có lỗi nào báo.
     *
     * Mặc định HIỆN: người mở màn lần đầu mà mọi hướng dẫn đã bị ẩn thì không có đường nào học được
     * luật; người đã quen chỉ phải bấm ẩn một lần.
     */
    const [hintsHidden, setHintsHidden] = useState(() => getLocalData(HINTS_HIDDEN_KEY, false) === true)

    const toggleHints = () => {
        const next = !hintsHidden

        setHintsHidden(next)
        setLocalData(HINTS_HIDDEN_KEY, next)
    }
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(null)
    const [materials, setMaterials] = useState([])
    const [materialError, setMaterialError] = useState(false)
    // Danh mục SKU: tìm phía SERVER. Xem `useSkuOptions` — nạp một lần rồi lọc tại chỗ sẽ không bao
    // giờ thấy SKU nằm ngoài trang đầu mà service trả.
    const {skus, skuError, skuSearching, skuSearchFailed, searchSkus} = useSkuOptions()
    const [dirty, setDirty] = useState(false)
    const [pasteTarget, setPasteTarget] = useState(null)
    const [reasonDeltas, setReasonDeltas] = useState(null)
    const [serverErrors, setServerErrors] = useState([])
    const [serverWarnings, setServerWarnings] = useState([])
    const [historyOpen, setHistoryOpen] = useState(false)

    const fetchDetail = useCallback(async () => {
        // Đổi công đoạn nhanh hai lần thì hai request chạy song song và KHÔNG bảo đảm về đúng thứ
        // tự. Ghim mã công đoạn đang hỏi vào ref: response nào không còn là cái mới nhất thì bỏ
        // hẳn, không ghi vào state. Thiếu chốt này thì trang có thể dừng ở dữ liệu của công đoạn
        // hỏi TRƯỚC trong khi URL đã là công đoạn khác.
        requestedStageRef.current = stageCode

        const isStale = () => requestedStageRef.current !== stageCode

        setLoading(true)
        setError(null)
        setServerErrors([])
        setServerWarnings([])

        try {
            const data = await getStageDetail(stageCode)

            if (isStale()) return

            if (!data) {
                // E-203 — công đoạn không tồn tại thì quay về SCR-M16-001.
                message.error('Không tìm thấy công đoạn')
                history.replace(ROUTES.STANDARD_TIME_STAGES)

                return
            }

            setDetail(data)
            setDetailFor(stageCode)
            setGroups(sortByPriority(data.groups || []))
            setDirty(false)
        } catch (e) {
            if (isStale()) return

            setError(e)
        } finally {
            if (!isStale()) setLoading(false)
        }
    }, [stageCode, history])

    useEffect(() => {
        fetchDetail()
    }, [fetchDetail])

    /**
     * Danh mục công đoạn cho ô chọn. Tách thành callback riêng để nút "Thử lại" ngay cạnh ô chọn gọi
     * lại được ĐÚNG request đã hỏng, thay vì buộc tải lại cả trang.
     *
     * Lỗi ở đây KHÔNG được rơi thành ô chọn rỗng: ô rỗng đọc y như "hệ thống chỉ có mỗi công đoạn
     * này", trong khi thật ra là mất danh mục.
     */
    const fetchStageOptions = useCallback(() => {
        setStagesError(null)

        return getStages()
            .then((list) => setStageOptions(list || []))
            .catch((e) => {
                setStageOptions([])
                setStagesError(e)
            })
    }, [])

    useEffect(() => {
        fetchStageOptions()

        // Danh mục NVL tải một lần cho mỗi phiên làm việc (§4.1). Lỗi thì khoá ô chọn NVL và hiện
        // E-204, phần còn lại của form vẫn sửa được.
        getMaterials()
            .then((list) => setMaterials(dedupeMaterials(list || [])))
            .catch(() => setMaterialError(true))

    }, [fetchStageOptions])

    // Không có quyền sửa: ẩn nút nhân bản / xóa / thêm / lưu, mọi ô nhập chuyển chỉ đọc (§5.3).
    const canEdit = canEditStandardConfig()
    const stage = detail ? detail.stage : null
    const byDetect = !!stage && stage.timeBasis === 'by_detect'
    const rateUnit = detail ? detail.rateUnit : 'second_per_item'
    const materialCodes = useMemo(() => materials.map((item) => item.materialCode), [materials])

    const originalRateById = useMemo(() => {
        const map = {}

        ;((detail && detail.groups) || []).forEach((group) => {
            map[group.id] = group.rate
        })

        return map
    }, [detail])

    /** Kiểm tra tại chỗ, tính lại sau mỗi thay đổi (V1 §3.3). `errors` chặn lưu, `warnings` thì không. */
    const {errors, warnings: localWarnings} = useMemo(
        () => validateGroups(groups, {materialCodes, byDetect}),
        [groups, materialCodes, byDetect]
    )

    /**
     * Nhóm ĐANG CHẠY ngay lúc này — tập con của `groups` mà mọi phép đếm "đã phủ / chưa phủ" phải
     * dùng.
     *
     * ⚠️ KHÔNG đếm trên `groups`. Từ khi service tách hai đường đọc, `stageDetail.groups` là tập
     *    CẤU HÌNH: nó cố ý gồm cả nhóm khai hiệu lực từ tháng sau (để người dùng thấy nhóm mình vừa
     *    lưu) lẫn nhóm đã hết hạn theo thời gian. Đếm cả những nhóm đó là nói "đã phủ" cho một NVL mà
     *    `lookupStandard`/M17 đang trả `no_standard` — đúng cái lệch mà service vừa sửa ba vòng, và
     *    lần nào cũng chết ở đây vì màn hình ghi đè câu trả lời đúng bằng phép tính trên cả tập.
     */
    const effectiveGroups = useMemo(() => groups.filter((group) => isEffectiveAt(group)), [groups])

    const hasAllGroup = effectiveGroups.some(isDefaultGroup)

    /**
     * NVL chưa được nhóm nào phủ. Cùng một dữ liệu này sinh ra V1.5 (cảnh báo) hoặc V1.6 (thông tin)
     * tuỳ công đoạn có nhóm ALL hay không (BR-M16-28) — đây là chỗ dễ cài sai nhất của vùng thông báo.
     */
    const uncoveredMaterials = useMemo(() => {
        if (!byDetect) return []

        // flatMap: một nhóm nay phủ được NHIỀU mã NVL cùng lúc.
        const covered = new Set(effectiveGroups.flatMap((group) => group.materialCodes || []))

        return materials.filter((item) => !covered.has(item.materialCode))
    }, [byDetect, effectiveGroups, materials])

    /** Nhóm có nhưng chưa/không còn chạy — lý do khiến các con số phía trên nhỏ hơn số thẻ trên màn. */
    const dormantCount = groups.length - effectiveGroups.length

    const markDirty = () => setDirty(true)

    const updateGroup = (index, next) => {
        setGroups((current) => current.map((group, position) => (position === index ? next : group)))
        markDirty()
    }

    const addGroup = () => {
        setGroups((current) => [
            ...current,
            {
                skuScope: 'all',
                skuList: [],
                materialCodes: [],
                rate: 1,
                // HÔM NAY, không phải ngày mai — và đi qua `dateOnlyIso` như mọi ô ngày khác.
                //
                // ⚠️ Hai lỗi cũ gộp ở một dòng. `nextWorkingDay()` làm nhóm vừa tạo LUÔN nằm ngoài
                //    tập đang-hiệu-lực tại thời điểm lưu, nên màn Danh sách báo ngay "— chưa có ALL"
                //    và cột Khoảng chỉ số trống, suốt phần còn lại của ngày: người dùng vừa cấu hình
                //    xong đọc ra "chưa xong / mất dữ liệu". Và `.toISOString()` trên một `Date` dựng
                //    theo giờ máy cho nửa đêm GIỜ VN = 17:00Z hôm trước, nên service (cắt ngày theo
                //    UTC) còn cảnh báo "ngày hiệu lực ở quá khứ" cho một ngày người dùng chưa từng chọn.
                effectiveFrom: dateOnlyIso(moment()),
                // Nhóm mới là số chưa ai đối chiếu với thực tế xưởng — khớp mặc định của service.
                verifyStatus: 'unverified',
                note: null,
                // Nhóm mới mặc định là mọi-SKU + mọi-NVL, tức NHÓM MẶC ĐỊNH — không đặt tên được
                // (VAL-19). Người dùng khai điều kiện xong mới mở khoá ô tên.
                name: null,
            },
        ])
        markDirty()
    }

    /**
     * Dời khoá của `collapsed` khi mảng `groups` bị chèn/xoá.
     *
     * `collapsed` khoá theo CHỈ SỐ mảng (render, `onToggle` và `toggleAll` đều dựa vào đó), nên mọi
     * thao tác làm dịch chỉ số phải dời khoá theo. Thiếu bước này thì xoá nhóm 1 trong 5 nhóm sẽ
     * làm cờ thu gọn của nhóm 2→5 nhảy lên nhóm 1→4: thẻ đang mở tự đóng, thẻ đang đóng tự bung.
     * Ở màn không phân trang (toàn bộ nhóm trên một màn) hiệu ứng đó rất dễ bị đọc thành
     * "màn hình mất dữ liệu".
     */
    const shiftCollapsed = (current, from, delta) => {
        const next = {}

        Object.keys(current).forEach((key) => {
            const position = Number(key)

            if (position < from) {
                next[position] = current[key]
            } else {
                next[position + delta] = current[key]
            }
        })

        return next
    }

    const duplicateGroup = (index) => {
        setGroups((current) => {
            const copy = {...current[index]}

            // Bản nhân bản là nhóm MỚI: bỏ id và version để service không hiểu là sửa nhóm cũ.
            delete copy.id
            delete copy.version

            // Trạng thái verify KHÔNG kế thừa: bản nhân bản sẽ được sửa điều kiện/chỉ số, nên dấu
            // "đã đối chiếu với xưởng" của nhóm gốc không còn nói đúng về nó. Ghi chú thì giữ —
            // nguồn số thường vẫn dùng được và người dùng sửa tiếp nếu cần.
            copy.verifyStatus = 'unverified'

            // Tên KHÔNG cần duy nhất nên chép nguyên vẫn lưu được — nhưng hai thẻ trùng tên nằm sát
            // nhau là mời sửa nhầm, đúng thứ tên nhóm sinh ra để tránh.
            copy.name = copyGroupName(copy.name, GROUP_NAME_MAX_LENGTH)

            return [...current.slice(0, index + 1), copy, ...current.slice(index + 1)]
        })

        // Bản nhân bản chèn vào ngay sau nhóm gốc, và để MỞ: nhân bản xong là để sửa điều kiện,
        // đóng lại thì người dùng phải bấm thêm một lần mới thấy thứ mình vừa tạo.
        setCollapsed((current) => ({...shiftCollapsed(current, index + 1, 1), [index + 1]: false}))
        markDirty()
    }

    const removeGroup = (index) => {
        setGroups((current) => current.filter((group, position) => position !== index))
        setCollapsed((current) => {
            const rest = {...current}

            delete rest[index]

            return shiftCollapsed(rest, index + 1, -1)
        })
        markDirty()
    }

    const toggleAll = () => {
        const anyOpen = groups.some((group, index) => !collapsed[index])
        const next = {}

        groups.forEach((group, index) => {
            next[index] = anyOpen
        })

        setCollapsed(next)
    }

    /** Bước 2 của luồng lưu (§4.3): so chỉ số mới với bản đang hiệu lực để biết có cần M2 không. */
    const rateDeltas = () => groups
        .map((group, index) => {
            const oldRate = originalRateById[group.id]

            if (oldRate === undefined) return null

            if (!rateChangeNeedsReason(oldRate, group.rate, RATE_CHANGE_THRESHOLD_PERCENT)) return null

            // `null` ở đây nghĩa là "lệch vô định" (chỉ số cũ là 0) — modal in câu riêng, không in %.
            return {index, oldRate, newRate: group.rate, deltaPercent: rateDeltaPercent(oldRate, group.rate)}
        })
        .filter(Boolean)

    const persist = async (changeReason) => {
        setSaving(true)
        setServerErrors([])
        // Dọn CẢ cảnh báo của lượt trước. Thiếu dòng này thì lưu lần 1 OK (kèm cảnh báo) rồi lần 2
        // hỏng sẽ hiện đồng thời khối vàng "Đã lưu, kèm lưu ý…" và khối đỏ báo lỗi — người dùng rất
        // dễ đọc thành "đã lưu rồi, chỉ có lỗi phụ" rồi rời màn hình và mất toàn bộ thay đổi.
        setServerWarnings([])

        try {
            const payload = {
                configUpdatedAt: detail.configUpdatedAt,
                changeReason,
                groups: groups.map((group) => ({
                    id: group.id,
                    skuScope: group.skuScope,
                    skuList: group.skuScope === 'list' ? group.skuList : [],
                    // Công đoạn tính theo item/đơn KHÔNG được gắn NVL (VAL-15 ở service).
                    materialCodes: byDetect ? group.materialCodes || [] : [],
                    rate: group.rate,
                    effectiveFrom: group.effectiveFrom,
                    effectiveTo: group.effectiveTo || null,
                    // ⚠️ Gửi `null` khi nhóm đang CHẤM TỰ ĐỘNG, không gửi `effectivePriority(group)`.
                    //    Hàm đó luôn trả ra một con số, nên gửi nó là biến MỌI nhóm thành đặt tay ở
                    //    service: từ đó nhóm không còn đổi theo luật chấm điểm nữa, và cả hai phía mất
                    //    khả năng phân biệt "hệ thống chấm 50" với "IE gõ 50".
                    priority: isManualPriority(group) ? group.priority : null,
                    // Khai luôn nguồn điểm thay vì để service suy từ `priority`. Service coi
                    // `prioritySource: 'computed'` là "bỏ qua số đã gửi" — nói thẳng ra thì hai phía
                    // không còn phụ thuộc vào quy ước ngầm "null nghĩa là tự chấm", và mẫu round-trip
                    // (tải `stageDetail` rồi gửi lại nguyên object) không thể vô tình đóng băng một
                    // nhóm tự chấm thành đặt tay.
                    prioritySource: isManualPriority(group) ? 'manual' : 'computed',
                    verifyStatus: group.verifyStatus || 'unverified',
                    note: group.note || null,
                    // `|| null` chứ không `?? null`: chuỗi rỗng (người dùng gõ rồi xoá hết) phải
                    // thành `null`, không phải `''` — service coi `''` là chưa đặt tên và chuẩn hoá
                    // lại, nhưng gửi `''` cho nhóm mặc định thì `.trim() !== ''` vẫn false nên vô
                    // hại; gửi `null` là nói đúng ý ngay từ đầu.
                    name: (group.name || '').trim() || null,
                })),
            }

            const saved = await saveStageGroups(stageCode, payload)

            setDetail(saved)
            setGroups((saved.groups || []).map((group) => ({...group})))
            setDirty(false)
            setReasonDeltas(null)
            message.success(
                `Đã lưu cấu hình công đoạn ${stage.name}. Có hiệu lực từ ${formatDate(groups[0] && groups[0].effectiveFrom)}, `
                + 'không cần duyệt. Bản cũ tự chuyển hết hiệu lực. Đã ghi nhật ký thay đổi.'
            )
            // Cảnh báo (VAL-08…) KHÔNG chặn lưu nên không thể trộn vào `serverErrors` — hiện riêng,
            // và hiện SAU khi lưu vì chỉ service mới biết. Ghi rõ "đã lưu" để không ai đọc thành lỗi.
            setServerWarnings(saved.warnings || [])
            window.scrollTo(0, 0)
        } catch (e) {
            // Lỗi đã được interceptor trải phẳng, đọc qua helper — xem helpers/apiError.js.
            const status = errorStatus(e)
            const code = errorCode(e)

            if (status === 409 || code === 'STAGE_CONFIG_STALE') {
                // E-202 — KHÔNG tự ghi đè. Giữ nguyên dữ liệu đang nhập, mời tải lại (EC-15).
                Modal.confirm({
                    title: 'Công đoạn này vừa được người khác cập nhật',
                    content: 'Tải lại để xem thay đổi mới nhất. Thao tác đang nhập của bạn sẽ mất.',
                    okText: 'Tải lại',
                    cancelText: 'Ở lại',
                    onOk: fetchDetail,
                })
            } else if (hasDetailCode(e, VAL_CODES.RATE_CHANGE_REASON_REQUIRED)) {
                // ⚠️ Đọc mã LUẬT trong `data[]`, KHÔNG phải `errorCode(e)` — cái đó luôn là
                //    `VALIDATION_ERROR` (mã envelope). So nó với tên luật là nhánh không bao giờ chạy,
                //    hộp thoại này không mở được, và người dùng kẹt: không còn đường nào lưu công đoạn.
                setReasonDeltas(errorDetails(e))
            } else if (hasDetailCode(e, VAL_CODES.AMBIGUOUS_PAIR)) {
                setServerErrors(
                    errorDetails(e).map((conflict) => ({
                        code: 'V-07',
                        message: `Cấu hình mơ hồ. Nhóm ${conflict.left.index + 1} ↔ Nhóm ${conflict.right.index + 1} `
                            + 'cùng độ ưu tiên và có thể cùng khớp một dòng NVL. Thêm điều kiện để phân biệt, hoặc gộp lại.',
                    }))
                )
            } else {
                // E-201 — giữ nguyên dữ liệu đang nhập, không mất thao tác.
                setServerErrors([{
                    code: 'E-201',
                    message: errorMessage(e) || 'Không lưu được cấu hình. Vui lòng thử lại',
                }])
            }
        } finally {
            setSaving(false)
        }
    }

    const handleSave = () => {
        if (errors.length) {
            // Có lỗi thì cuộn tới vùng thông báo, KHÔNG gọi hệ thống và không ghi gì (§4.3 bước 1).
            const anchor = document.querySelector('.VcValidationArea')

            if (anchor) anchor.scrollIntoView({behavior: 'smooth', block: 'center'})

            return
        }

        const deltas = rateDeltas()

        if (deltas.length) {
            setReasonDeltas(deltas)

            return
        }

        persist()
    }

    /** Hộp thoại M3 — xác nhận rời màn khi có thay đổi chưa lưu. */
    const confirmLeave = (onConfirm) => {
        if (!dirty) {
            onConfirm()

            return
        }

        Modal.confirm({
            title: 'Rời khỏi màn hình?',
            content: 'Bạn có thay đổi chưa lưu. Rời khỏi màn hình sẽ mất toàn bộ thay đổi.',
            okText: 'Rời và bỏ thay đổi',
            okType: 'danger',
            cancelText: 'Ở lại',
            onOk: onConfirm,
        })
    }

    /**
     * Nhãn nhóm cho một cảnh báo của service.
     *
     * ⚠️ `warning.row` là vị trí trong payload **GỬI LÊN**, KHÔNG phải trong `groups` đang hiển thị.
     *    Hai mảng không trùng nhau: `stageDetail` sắp lại theo độ ưu tiên giảm dần, và nhóm bị gỡ khỏi
     *    lượt lưu đã chuyển `status: 'expired'` nên rụng khỏi response. Tra `row` vào `groups` thì nhẹ là
     *    chỉ SAI nhóm (người dùng đi sửa nhầm), nặng là trỏ vào nhóm không tồn tại — cảnh báo "Nhóm 2"
     *    trong khi màn hình chỉ còn một thẻ.
     *
     *    Nên ưu tiên `warning.scope` (mô tả phạm vi do service gửi kèm, ví dụ "Mọi SKU · NVL 0205"):
     *    nó gọi đúng tên nhóm ở cả hai đầu mà không phụ thuộc chỉ số. `row` chỉ còn là phương án cuối.
     */
    const warningLabel = (warning) => {
        if (warning.scope) return warning.scope

        const group = groups[warning.row - 1]

        if (group && isDefaultGroup(group)) return DEFAULT_GROUP_LABEL

        // Nhóm đã đặt tên thì gọi bằng tên đó — đúng lý do tên nhóm sinh ra: nối được dòng cảnh báo
        // với thẻ trên màn hình. Giữ SỐ kèm theo vì header cũng hiện số, và cảnh báo V-07 gọi theo số.
        // `.trim()` chứ không chỉ truthy: tên toàn khoảng trắng cho ra "    (nhóm 2)" — đúng thứ
        // câu trên vừa nói là phải tránh, và không nối được với thẻ nào cả.
        const ten = (group && group.name ? group.name : '').trim()

        if (ten) return `${ten} (nhóm ${warning.row})`

        return `Nhóm ${warning.row}`
    }

    const renderValidationArea = () => {
        const blocks = []

        const byCode = (code) => errors.filter((item) => item.code === code)

        // Cảnh báo tại chỗ: hiện nhưng KHÔNG chặn nút Lưu. Ví dụ mã NVL đã bị gỡ khỏi danh mục —
        // service vẫn ghi được, nên khoá người dùng lại là khoá nhầm.
        if (localWarnings.length) {
            blocks.push(
                <Notice key="local-warn" level="warning">
                    {localWarnings.map((w) => (
                        <div key={`${w.code}-${w.message}`}>{w.message}</div>
                    ))}
                </Notice>
            )
        }

        // V1.1 / V1.2 / V1.3 — gom theo mã để câu thông báo nói "{n} nhóm", đúng như §3.3.
        const emptySkuList = byCode('V-01')

        if (emptySkuList.length) {
            blocks.push(
                <Notice key="v1.1" level="error">
                    {emptySkuList.length} nhóm chọn ‘Danh sách cụ thể’ nhưng chưa nhập SKU. SKU là lựa
                    chọn bắt buộc.
                </Notice>
            )
        }

        /*
          * KHÔNG còn khối riêng cho V-02.
          *
          * V-02 (NVL không có trong danh mục) đã chuyển từ `errors` sang `warnings`
          * (`helpers/priority.js:258` — "V-02 — KHÔNG chặn lưu"), nên `byCode('V-02')` lọc trên
          * `errors` luôn trả mảng rỗng và khối Notice cũ là code chết. Tệ hơn, nội dung nó định in
          * ("Phải chọn từ danh sách…") mô tả một chính sách CHẶN LƯU không còn đúng nữa.
          *
          * V-02 giờ ra qua `localWarnings` như mọi cảnh báo không chặn khác. `renderedAbove` bên
          * dưới cũng đã bỏ 'V-02' — nếu sau này service có trả V-02 về trong `errors` thì nó rơi
          * xuống khối generic và vẫn hiện, thay vì bị nuốt im lặng.
          */

        findConflicts(groups).forEach(({left, right}, index) => {
            blocks.push(
                <Notice key={`v1.3-${index}`} level="error">
                    Cấu hình mơ hồ. Nhóm {left.index + 1} ↔ Nhóm {right.index + 1} cùng độ ưu tiên và
                    có thể cùng khớp một dòng NVL. Thêm điều kiện để phân biệt, hoặc gộp lại.
                </Notice>
            )
        })

        /**
         * Mọi lỗi CÒN LẠI đều phải hiện — danh sách loại trừ, không phải danh sách cho phép.
         *
         * ⚠️ Trước đây chỗ này liệt kê tên từng mã được render (`V-04`, `V-06`, `V-08`). Thêm luật mới
         *    mà quên thêm vào đây là lỗi đó CHẶN nút Lưu nhưng không hiện gì: người dùng bấm Lưu, trang
         *    cuộn lên vùng thông báo rỗng, không request nào chạy, không manh mối nào để lần ra. Đúng
         *    chuyện đã xảy ra với V-09 (`effectiveTo` <= `effectiveFrom`) — ca rất dễ gặp vì cận trên
         *    loại trừ nên đặt hai ngày bằng nhau là hợp lý với người dùng.
         *    Nay mặc định là HIỆN; chỉ những mã đã được render bằng câu gộp riêng ở trên mới loại ra.
         */
        // V-01/V-02 đã có câu gộp "{n} nhóm ..."; V-07 do khối `findConflicts` ở trên in ra theo CẶP
        // nhóm, chi tiết hơn hẳn message phẳng.
        //
        // V-08 (hai nhóm ALL chồng lấn) là TRƯỜNG HỢP RIÊNG của V-07 chứ không phải luật thứ hai:
        // hai nhóm ALL luôn cùng điểm 0, phạm vi giao nhau, nên `findConflicts` đã bắt và in ra cặp
        // đó rồi. Để V-08 rơi xuống khối generic là hai Notice đỏ cho một lỗi duy nhất, và người dùng
        // phải đoán xem có phải sửa hai chỗ không. Nó vẫn CHẶN nút Lưu — chỉ là không nói hai lần.
        const renderedAbove = ['V-01', 'V-07', 'V-08']

        errors
            .filter((item) => renderedAbove.indexOf(item.code) === -1)
            .forEach((item, index) => {
                blocks.push(<Notice key={`v-${item.code}-${index}`} level="error">{item.message}</Notice>)
            })

        serverErrors.forEach((item, index) => {
            blocks.push(<Notice key={`srv-${index}`} level="error">{item.message}</Notice>)
        })

        // V1.4 — có nhóm nhưng không có nhóm ALL.
        if (groups.length && !hasAllGroup) {
            blocks.push(
                <Notice key="v1.4" level="warning">
                    Công đoạn chưa có nhóm mặc định (mọi SKU + mọi NVL) → hiện có{' '}
                    {formatInt((detail && detail.missingSkuCount) || 0)} SKU đang
                    bật thiếu định mức ở đây. Thêm một nhóm mặc định là cách chắc chắn nhất để không
                    SKU nào bị sót.
                </Notice>
            )
        }

        if (uncoveredMaterials.length) {
            // Tách tên và mã thành hai trường thay vì ghép sẵn chuỗi: SkuIdList cần đếm được số
            // phần tử bị ẩn, và cần dựng mã thành chữ mờ riêng bên trong từng chip.
            const materialItems = uncoveredMaterials.map((item) => ({
                label: item.name,
                code: item.materialCode,
            }))

            // V1.5 và V1.6 dùng CÙNG một dữ liệu nhưng khác mức thông báo (§0.3 · BR-M16-28).
            if (!hasAllGroup) {
                blocks.push(
                    <Notice key="v1.5" level="warning">
                        {formatInt(uncoveredMaterials.length)} NVL chưa được nhóm nào phủ. Công đoạn
                        này lại không có nhóm mặc định nên dòng NVL đó sẽ rơi vào no_standard.
                        <SkuIdList ids={materialItems} unit="NVL"/>
                    </Notice>
                )
            } else {
                blocks.push(
                    <Notice key="v1.6" level="info">
                        {formatInt(uncoveredMaterials.length)} NVL chưa có nhóm riêng → đang dùng chỉ
                        số của nhóm mặc định. Nếu tốc độ của các NVL này khác đáng kể thì nên tách nhóm
                        riêng.
                        <SkuIdList ids={materialItems} unit="NVL"/>
                    </Notice>
                )
            }
        }

        return blocks
    }

    /**
     * `detail` có đúng là của công đoạn đang mở hay không.
     *
     * Mọi thứ đọc từ `detail` (thanh thuộc tính, danh sách nhóm, nút Lưu) phải đi qua cờ này. Bỏ nó
     * là quay lại đúng lỗi cũ: đổi công đoạn thì tiêu đề đổi ngay còn bảng nhóm vẫn của công đoạn
     * trước — và lần Lưu tiếp theo ghi nhóm của công đoạn khác sang.
     */
    const detailReady = !!stage && detailFor === stageCode

    // Tên công đoạn cho tiêu đề: ưu tiên `detail` khi nó đã đúng công đoạn, nếu chưa thì lấy từ danh
    // mục để đầu trang không nhảy chữ giữa hai lần tải.
    const stageName = detailReady
        ? stage.name
        : (stageOptions.find((item) => item.stageCode === stageCode) || {}).name

    /** Nội dung chỉ có nghĩa khi `detail` đã đúng công đoạn — xem `detailReady`. */
    const renderStageContent = () => (
        <>
                {byDetect && (
                    <Notice level="info">
                        <div>
                            Một SKU có thể xuất hiện ở nhiều nhóm tại cùng công đoạn. Vì SKU được cấu thành
                            từ nhiều nguyên vật liệu, mỗi NVL có chỉ số riêng. Ví dụ SKU có cả gỗ 5 ly và
                            mica 2 ly thì ở công đoạn Cắt sẽ khớp hai nhóm khác nhau, mỗi nhóm áp cho một
                            dòng NVL. Đây là hành vi đúng, không phải lỗi cấu hình.
                        </div>
                        <div className="mt-2">
                            Không phải NVL nào cũng qua đủ công đoạn — gỗ chỉ cắt không in, mica có in có
                            cắt. Hệ thống chỉ cảnh báo công đoạn nào thiếu định mức; việc trong một công
                            đoạn đã cấu hình đủ loại NVL hay chưa do người dùng chủ động quản lý.
                        </div>
                    </Notice>
                )}

                <div className="VcStageBar">
                    <div className="VcStageBarItem">
                        <label>Nhịp công đoạn</label>
                        <div>{(PACING_LABELS[stage.pacing] || {}).long}</div>
                    </div>
                    <div className="VcStageBarItem">
                        <label>Đơn vị đếm sản lượng</label>
                        {/* BR-M16-32: thuộc tính của công đoạn, không cho sửa ở màn này. */}
                        <div>
                            {stage.countUnit === 'order' ? 'Đơn' : 'Item'} — chỉ số hiển thị ở cột Sản lượng
                        </div>
                    </div>
                    <div className="VcStageBarItem">
                        <label>Cách tính giờ</label>
                        <div>
                            {byDetect
                                ? `Theo dữ liệu detect — cộng theo từng dòng NVL (${stage.detectField})`
                                : 'Theo item/đơn — rate × số lượng'}
                        </div>
                    </div>
                    <div className="VcStageBarItem">
                        <label>Action ghi nhận</label>
                        <div>
                            <ActionCodes codes={stage.actionCodes} actionCode={stage.actionCode}/>
                        </div>
                    </div>
                    <div className="VcStageBarItem">
                        <label>Số nhóm điều kiện</label>
                        {/*
                          * Nói CẢ HAI con số khi chúng lệch nhau. "3 nhóm — chưa có ALL" một mình là
                          * câu đánh đố khi nhóm ALL có thật nhưng chưa tới ngày hiệu lực: người dùng
                          * nhìn thấy thẻ ALL ngay bên dưới và kết luận màn hình hỏng, rồi thêm nhóm
                          * ALL thứ hai — `PUT` trả 400 VAL-09 và họ mắc kẹt.
                          */}
                        <div>
                            {groups.length} nhóm
                            {!!dormantCount && (
                                <span className="VcMuted"> ({formatInt(dormantCount)} chưa/không còn hiệu lực)</span>
                            )}
                            {hasAllGroup ? ' (có nhóm mặc định)' : ' — chưa có nhóm mặc định đang chạy'}
                        </div>
                    </div>
                </div>

                <Notice level="warning">
                    Cách đo: nhập thời gian quan sát thực tế trên một đơn vị đo, đã bao gồm nghỉ ngơi và
                    gián đoạn nhỏ.
                    {stageCode === 'cat' && ' Riêng cắt: đo trọn chu trình, gồm cả nhặt và phân loại mảnh sau khi cắt.'}
                </Notice>

                {materialError && (
                    <Notice level="error">
                        Không tải được danh sách nguyên vật liệu. Ô chọn NVL đang bị khoá, các phần khác
                        vẫn sửa được.
                    </Notice>
                )}

                {/*
                  * Trước đây lỗi này bị nuốt hoàn toàn (`catch(() => setSkus([]))`): ô chọn "Danh sách cụ
                  * thể" không gợi ý mã nào và người dùng đọc thành "danh mục SKU trống", trong khi thật ra
                  * là request hỏng. Nói rõ là lỗi TẢI, và nói rõ đường đi tiếp vẫn còn.
                  */}
                {!!serverWarnings.length && (
                <Notice level="warning">
                    <div><strong>Đã lưu</strong>, kèm lưu ý:</div>
                    {serverWarnings.map((w, index) => (
                        <div key={`${w.code}-${w.row}-${index}`} className="VcNoticeSub">
                            {warningLabel(w)}: {w.message}
                        </div>
                    ))}
                </Notice>
            )}

            {skuError && (
                    <Notice level="warning">
                        Không tải được danh mục SKU nên ô chọn “Danh sách cụ thể” không gợi ý được mã — đây
                        là lỗi tải danh mục, KHÔNG phải danh mục rỗng. Vẫn gõ tay hoặc dán hàng loạt được;
                        hệ thống kiểm mã khi lưu.
                    </Notice>
                )}

                {!!dormantCount && (
                    <Notice level="info">
                        {formatInt(dormantCount)} nhóm bên dưới <strong>chưa tới</strong> hoặc{' '}
                        <strong>đã qua</strong> ngày hiệu lực nên không được tính vào các cảnh báo độ
                        phủ ở đây — chúng vẫn được lưu và vẫn sửa được. Thẻ nhóm có nhãn riêng ở góc.
                    </Notice>
                )}

                <div className="VcValidationArea">{renderValidationArea()}</div>

                <div className="VcGroupList">
                    {groups.map((group, index) => (
                        <GroupCard
                            key={group.id || `new-${index}`}
                            group={group}
                            index={index}
                            materials={materials}
                            skus={skus}
                            skuSearching={skuSearching}
                            skuSearchFailed={skuSearchFailed}
                            onSkuSearch={searchSkus}
                            rateUnit={rateUnit}
                            byDetect={byDetect}
                            collapsed={!!collapsed[index]}
                            readOnly={!canEdit}
                            onToggle={() => setCollapsed({...collapsed, [index]: !collapsed[index]})}
                            onChange={(next) => updateGroup(index, next)}
                            onDuplicate={() => duplicateGroup(index)}
                            onRemove={() => removeGroup(index)}
                            onPasteSku={() => setPasteTarget(index)}
                        />
                    ))}

                    {!groups.length && (
                        <div className="VcEmptyGroups">
                            Công đoạn này chưa có nhóm điều kiện nào. Thêm một nhóm mặc định (mọi SKU, không khai NVL) là cách chắc chắn
                            nhất để không SKU nào bị sót.
                        </div>
                    )}

                    {canEdit && (
                        <button type="button" className="VcAddGroup" onClick={addGroup}>
                            <PlusOutlined/> Thêm nhóm điều kiện
                        </button>
                    )}
                </div>

                <div className="VcSaveBar">
                    <span className={dirty ? 'VcWarning' : 'VcMuted'}>
                        {dirty ? 'Có thay đổi chưa lưu' : 'Chưa có thay đổi chưa lưu'}
                    </span>
                    <div>
                        <Button onClick={() => confirmLeave(() => history.push(ROUTES.STANDARD_TIME_STAGES))}>Hủy</Button>
                        {canEdit && (
                            <Button type="primary" loading={saving} onClick={handleSave}>
                                Lưu cấu hình công đoạn
                            </Button>
                        )}
                    </div>
                </div>

                <PasteSkuModal
                    visible={pasteTarget !== null}
                    onCancel={() => setPasteTarget(null)}
                    onAdd={(skuIds) => {
                        const index = pasteTarget
                        const group = groups[index]
                        const merged = [...new Set([...(group.skuList || []), ...skuIds])]

                        // KHÔNG đóng hộp thoại ở đây: PasteSkuModal tự đóng khi không có mã nào
                        // bị loại, và giữ mở để báo V-10/V-11 khi có.
                        updateGroup(index, {...group, skuScope: 'list', skuList: merged})
                    }}
                />

                <ChangeReasonModal
                    visible={!!reasonDeltas}
                    deltas={reasonDeltas}
                    loading={saving}
                    // "Quay lại" hủy TOÀN BỘ thao tác lưu, không chỉ đóng hộp thoại (§4.3 bước 3).
                    onCancel={() => setReasonDeltas(null)}
                    onConfirm={(reason) => persist(reason)}
                />

                <div className="VcRateUnitFoot VcMuted">
                    Đơn vị chỉ số của công đoạn này: giây / {RATE_UNIT_LABELS[rateUnit]}
                </div>
        </>
    )

    /**
     * Vùng nội dung phụ thuộc `detail`. Tách riêng để đầu trang và ô chọn công đoạn KHÔNG bị thay
     * bằng vòng xoay hay bằng khối lỗi:
     *   · vòng xoay toàn trang làm mất luôn ô chọn mà người dùng vừa bấm — đổi công đoạn xong là
     *     trang trắng, muốn đổi tiếp phải đợi;
     *   · khối lỗi toàn trang còn tệ hơn: tải hỏng ở công đoạn A thì không còn đường sang công đoạn
     *     B, phải bấm back.
     */
    const renderStageBody = () => {
        if (error) {
            return (
                <div className="VcStageBody">
                    <LoadError error={error} onRetry={fetchDetail}/>
                </div>
            )
        }

        if (loading || !detailReady) {
            return (
                <div className="VcStageBody VcStageBody--loading">
                    <Spin tip="Đang tải cấu hình công đoạn…"/>
                </div>
            )
        }

        return renderStageContent()
    }


    return (
        <div className={`VcPage VcStageConfigPage container-fluid${hintsHidden ? ' VcStageConfigPage--noHints' : ''}`}>
            <div className="VcPageHead">
                <h1 className="VcPageTitle">
                    Cấu hình công đoạn: {stageName || <span className="VcMuted">{stageCode}</span>}
                </h1>
                <div className="VcPageActions">
                    <Button disabled={!detailReady} onClick={toggleAll}>Thu gọn / Mở tất cả</Button>
                    <Button onClick={toggleHints}>{hintsHidden ? 'Hiện gợi ý' : 'Ẩn gợi ý'}</Button>
                    {/* Không khoá theo `detailReady`: lịch sử chỉ cần `stageCode`, xem được cả khi
                        phần cấu hình đang tải hoặc tải hỏng — đó đúng là lúc người ta cần nó nhất. */}
                    <Button onClick={() => setHistoryOpen(true)}>Lịch sử</Button>
                    {/* Tra cứu theo SKU giờ là MODAL của màn danh sách, không còn route riêng —
                        nên điều hướng về màn đó kèm cờ `?lookup=1` để nó tự mở modal. */}
                    <Button
                        onClick={() => confirmLeave(
                            () => history.push(`${ROUTES.STANDARD_TIME_STAGES}?lookup=1`)
                        )}
                    >
                        Tra cứu theo SKU
                    </Button>
                </div>
            </div>

            <div className="VcStageBar">
                <div className="VcStageBarItem">
                    <label>Công đoạn</label>
                    <Select
                        className="VcStageSelect"
                        value={stageCode}
                        // Danh mục hỏng thì vẫn giữ ô chọn ở giá trị hiện tại (đọc được đang ở đâu),
                        // chỉ khoá không cho đổi — cho đổi sang một danh sách rỗng là bẫy.
                        disabled={!!stagesError}
                        onChange={(value) => confirmLeave(() => history.push(stageConfigPath(value)))}
                    >
                        {stageOptions.map((item) => (
                            <Option key={item.stageCode} value={item.stageCode}>{item.name}</Option>
                        ))}
                    </Select>
                    {!!stagesError && (
                        <div className="VcFieldFoot">
                            <span className="VcDanger">Không tải được danh mục công đoạn.</span>
                            <Button size="small" onClick={fetchStageOptions}>Thử lại</Button>
                        </div>
                    )}
                </div>
            </div>

            {renderStageBody()}

            <HistoryDrawer
                stageCode={stageCode}
                stageName={stageName}
                visible={historyOpen}
                onClose={() => setHistoryOpen(false)}
            />
        </div>
    )
}

export default StageConfigPage
